import React, { useCallback, useEffect, useState } from 'react';
import { Grid, Box, Button } from '@mui/material';
import styles from './payouts.module.scss';
import { GenericTable } from 'components/common/GenericTable';
import { getAllPayouts } from '_redux/services/categories.service';
import { getPayoutsColumns } from 'utils/Payouts';
import { PDFDownloadLink, pdf } from '@react-pdf/renderer'; // Importing PDF utilities
import PdfViewerModal from 'components/PdfViewerModal/PdfViewerModal';
import { authActions } from '_redux/slices/auth.slice';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { SEND_MAIL } from '_redux/types/mail.types';

const API_URL = process.env.REACT_APP_API_URL;

const Payouts = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [data, setData] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [advisor, setAdvisor] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState({});

  const { user } = useSelector(authActions.selectors.getUser);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [selectedMonthTo, setSelectedMonthTo] = useState(dayjs());
  const dispatch = useDispatch();

  const getCategoriesData = useCallback(async () => {
    const response = await getAllPayouts(
      page,
      rowsPerPage,
      selectedMonth.format('YYYY-MM-DD'),
      selectedMonthTo.format('YYYY-MM-DD')
    );
    const total = response.total;
    setTotalData(total);

    const lastPage = Math.ceil(total / rowsPerPage) - 1;
    if (page === lastPage) {
      const remainingItems = total % rowsPerPage || rowsPerPage;
      setData(response.data.slice(0, remainingItems));
    } else {
      setData(response.data);
    }
  }, [page, rowsPerPage, selectedMonth, selectedMonthTo]);

  useEffect(() => {
    getCategoriesData();
  }, [getCategoriesData]);

  const handleApprove = async row => {
    try {
      const response = await fetch(API_URL + '/api/payouts/updatePayout', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: row.id,
          updatedFields: {
            isApproved: true,
          },
        }),
      });

      if (response.ok) {
        getCategoriesData(); // Refresh the table
      }
    } catch (error) {
      console.error('Error approving payout:', error);
    }
  };

  const handleView = row => {
    setAdvisor(row);
    openPdfInNewTab(row);
  };

  const generatePdfBlob = row => {
    return new Promise((resolve, reject) => {
      const pdfDoc = pdf(
        <PdfViewerModal
          user={user}
          data={row}
          from={selectedMonth}
          to={selectedMonthTo}
        />
      );
      pdfDoc
        .toBlob()
        .then(blob => resolve(blob))
        .catch(error => reject(error));
    });
  };

  const openPdfInNewTab = row => {
    // Generate PDF with @react-pdf/renderer
    const pdfDoc = pdf(
      <PdfViewerModal
        user={user}
        data={row}
        from={selectedMonth}
        to={selectedMonthTo}
      />
    );

    // Get the generated PDF as a Blob
    pdfDoc.toBlob().then(blob => {
      // Create a URL for the Blob
      const pdfUrl = URL.createObjectURL(blob);

      // Open a new tab and display the PDF
      const newTab = window.open('', '_blank');
      if (newTab) {
        newTab.document.title = 'Payout Details';
        newTab.document.body.innerHTML = `
          <div id="pdf-viewer-container" style="height: 100%; width: 100%; overflow: hidden;">
            <iframe src="${pdfUrl}" width="100%" height="100%" style="border: none;"></iframe>
          </div>
        `;
      }
    });
  };

  const handleSendPdf = async row => {
    setLoadingPdf(prev => ({ ...prev, [row.id]: true }));
    try {
      // Generate the PDF blob
      const pdfBlob = await generatePdfBlob(row);

      // Create FormData to send the blob
      const formData = new FormData();
      formData.append('payoutId', row.id);
      formData.append('email', row.email);
      formData.append('pdf', pdfBlob, `payout-${row.id}.pdf`);

      const response = await fetch(`${API_URL}/api/payouts/send-pdf`, {
        method: 'POST',
        body: formData, // Send as FormData instead of JSON
      });

      if (response.ok) {
        toast.success(`PDF for payout ${row.id} sent to advisor successfully!`);

        const formData1 = new FormData();

        formData1.append('receipentName', 'Admin');
        formData1.append('senderName', `${user.firstName} ${user?.lastName}`);
        formData1.append('senderEmail', row.email);
        formData1.append('receipentEmail', 'admin@gmail.com');
        formData1.append('subject', 'Payout Details');
        formData1.append('heading', 'Payout Details');
        formData1.append('description', 'Payout Details');
        formData1.append('footer', 'Looking forward to hearing from you');

        if (pdfBlob) {
          formData1.append('file', pdfBlob);
        }

        dispatch({ type: SEND_MAIL, payload: formData1 });
      } else {
        const errorData = await response.json();
        toast.error(
          `Failed to send PDF: ${errorData.error || 'Unknown error'}`
        );
      }
    } catch (error) {
      console.error('Error sending PDF:', error);
      toast.error('Error sending PDF. Please try again.');
    } finally {
      setLoadingPdf(prev => ({ ...prev, [row.id]: false }));
    }
  };

  const columns = getPayoutsColumns({
    onView: handleView,
    onApprove: handleApprove,
    onSendPdf: handleSendPdf,
    loadingPdf,
  });

  return (
    <>
      <Box className={styles.tableContainer}>
        <Grid container>
          <Grid m={4} item xs={12}>
            <GenericTable
              title={'Statements'}
              refreshData={getCategoriesData}
              setData={setData}
              pageConfig={{
                page,
                setPage,
                rowsPerPage,
                setRowsPerPage,
                totalRows: totalData,
              }}
              addNewProduct={false}
              onAddNewProduct={() => {}}
              columns={columns}
              data={data}
              isPayouts={true}
              isDate={true}
              fetchType="statements"
              setSelectedMonth={setSelectedMonth}
              selectedMonth={selectedMonth}
              selectedMonthTo={selectedMonthTo}
              setSelectedMonthTo={setSelectedMonthTo}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default Payouts;
